import type { UseFetchOptions } from 'nuxt/app'
import type {
  EventListPageTypeString,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'
import { useKftApiFetch } from '~/composables/useKftApiFetch'
import { getEventListPageStrapiURL } from '~/utils/strapiURLhelpers'

export const useFetchEventListPageDynamicContent = (
  pageType: EventListPageTypeString,
  {
    citySlug,
    categorySlug,
    generalSlug,
  }: { citySlug?: string; categorySlug?: string; generalSlug?: string }
) => {
  const pageSlug = {
    [EventListPageType.CITY]: citySlug,
    [EventListPageType.CATEGORY]: categorySlug,
    [EventListPageType.CITY_CATEGORY]: `${citySlug}-${categorySlug}`,
    [EventListPageType.GENERAL]: generalSlug,
    [EventListPageType.TEAMEVENT]: generalSlug,
  }

  const mapResource = {
    [EventListPageType.CITY]: 'Locality',
    [EventListPageType.CATEGORY]: 'Category',
    [EventListPageType.CITY_CATEGORY]: 'LocalityCategory',
    [EventListPageType.GENERAL]: 'Tag',
    [EventListPageType.TEAMEVENT]: 'Tag',
  }

  return useKftApiFetch<KftApiDefaultResponse<KftDynamicContentMetrics>>(
    `/v1/store/metrics/${mapResource[pageType]}/${pageSlug[pageType]}`
  )
}

export function useStrapiEventListPage({
  slug,
  citySlug,
  categorySlug,
  options = {},
  locale,
}: {
  slug: string
  citySlug?: string
  categorySlug?: string
  options?: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiEventListPage[]>,
    StrapiEventListPage[]
  >
  locale?: string
}) {
  const { locale: currentLocale, locales } = useI18n()

  // If locale is explicitly provided, fetch only for that locale
  if (locale) {
    const eventListPageURL = getEventListPageStrapiURL(
      locale,
      slug,
      citySlug,
      categorySlug
    )

    return useStrapi<StrapiEventListPage>({
      urlSuffix: eventListPageURL,
      options,
    })
  }

  // When no locale is provided:
  // 1. First try to fetch for current locale (fast path)
  // 2. If no results found, fetch for all locales to enable redirect behavior
  //    (e.g., /en-us/koeln/toepferkurs/ should redirect to /en-us/koeln/pottery-class/)
  return useAsyncData<StrapiEventListPage[] | null>(
    `event-list-pages-${slug}-${citySlug}-${categorySlug}`,
    async () => {
      // First attempt: fetch for current locale only
      const currentLocaleURL = getEventListPageStrapiURL(
        currentLocale.value,
        slug,
        citySlug,
        categorySlug
      )

      const currentLocaleResult = await useStrapi<StrapiEventListPage>({
        urlSuffix: currentLocaleURL,
        options,
      })

      const currentLocalePages = currentLocaleResult.data.value || []

      // If we found pages for the current locale, return them immediately
      if (currentLocalePages.length > 0) {
        return currentLocalePages
      }

      // No pages found for current locale - fetch all locales to find wrong-locale page
      // This enables redirect behavior when URL uses a slug from another locale
      const fetchPromises = locales.value.map(async (locale) => {
        const eventListPageURL = getEventListPageStrapiURL(
          locale.code,
          slug,
          citySlug,
          categorySlug
        )

        const result = await useStrapi<StrapiEventListPage>({
          urlSuffix: eventListPageURL,
          options,
        })

        return result.data.value || []
      })

      const results = await Promise.all(fetchPromises)
      const allPages = results.flat()

      // Return all pages found across all locales
      // useTransactionalPageResource will handle locale matching and redirects
      return allPages
    },
    {
      server: true,
      lazy: false,
    }
  )
}
